/**
 * Parser de markup inline para texto narrativo.
 *
 * Tags soportados (no anidables entre sí):
 *   [shake]...[/shake]            — tremor visual
 *   [glow color=ember]...[/glow]  — resplandor (color: ember|mystic|moss|ash)
 *   [fade]...[/fade]              — fade-in suave
 *   [emph]...[/emph]              — énfasis (itálica)
 *   [whisper]...[/whisper]        — texto pequeño atenuado
 *   [pipo]...[/pipo]              — acción/reacción de Pipo (color + itálica)
 *   [typewriter speed=slow]...[/typewriter] — forzar velocidad distinta en un tramo
 *
 * Los tags desconocidos se dejan como texto plano (no se rompe nada).
 * Los tags abiertos sin cerrar también se dejan como texto plano.
 */

export type MarkupTagName =
  | 'shake'
  | 'glow'
  | 'fade'
  | 'emph'
  | 'whisper'
  | 'pipo'
  | 'typewriter';

export type MarkupNode =
  | { type: 'text'; value: string }
  | { type: 'tag'; name: MarkupTagName; attrs: Record<string, string>; value: string };

const TAG_REGEX = /\[([a-z]+)(\s[^\]]*)?\]([\s\S]*?)\[\/\1\]/g;

const TAG_CONOCIDOS: ReadonlySet<MarkupTagName> = new Set([
  'shake',
  'glow',
  'fade',
  'emph',
  'whisper',
  'pipo',
  'typewriter',
]);

function esTagConocido(nombre: string): nombre is MarkupTagName {
  return TAG_CONOCIDOS.has(nombre as MarkupTagName);
}

function parseAttrs(raw: string): Record<string, string> {
  const attrs: Record<string, string> = {};
  const re = /([a-z][a-z0-9_]*)\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s\]]+))/gi;
  let m: RegExpExecArray | null;
  while ((m = re.exec(raw)) !== null) {
    const key = m[1].toLowerCase();
    const value = m[2] ?? m[3] ?? m[4] ?? '';
    attrs[key] = value;
  }
  return attrs;
}

/**
 * Parsea markup inline en una lista de nodos. El texto sin tags
 * queda como nodos `text`; los spans con tags conocidos quedan como
 * nodos `tag`; los tags desconocidos se concatenan como texto plano.
 */
export function parseMarkup(input: string): MarkupNode[] {
  if (!input) return [];
  const nodes: MarkupNode[] = [];
  let lastIndex = 0;
  TAG_REGEX.lastIndex = 0;

  let match: RegExpExecArray | null;
  while ((match = TAG_REGEX.exec(input)) !== null) {
    if (match.index > lastIndex) {
      nodes.push({ type: 'text', value: input.slice(lastIndex, match.index) });
    }
    const nombre = match[1];
    const attrs = parseAttrs(match[2] ?? '');
    const contenido = match[3];
    if (esTagConocido(nombre)) {
      nodes.push({ type: 'tag', name: nombre, attrs, value: contenido });
    } else {
      // Tag desconocido → mantener el match original como texto.
      nodes.push({ type: 'text', value: match[0] });
    }
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < input.length) {
    nodes.push({ type: 'text', value: input.slice(lastIndex) });
  }

  return fusionarTextAdyacentes(nodes);
}

/** Une nodos text consecutivos en uno solo. */
function fusionarTextAdyacentes(nodes: MarkupNode[]): MarkupNode[] {
  const resultado: MarkupNode[] = [];
  for (const n of nodes) {
    const ultimo = resultado[resultado.length - 1];
    if (n.type === 'text' && ultimo && ultimo.type === 'text') {
      ultimo.value += n.value;
    } else {
      resultado.push(n);
    }
  }
  return resultado;
}

/** Cantidad total de caracteres visibles de una lista de nodos. */
export function contarCaracteres(nodes: MarkupNode[]): number {
  return nodes.reduce((suma, n) => suma + n.value.length, 0);
}

/** Texto plano (útil para accesibilidad y ARIA labels). */
export function aTextoPlano(nodes: MarkupNode[]): string {
  return nodes.map((n) => n.value).join('');
}
